import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { loggedOutUser } from '../context/context';
import { rememberUsername } from '../helpers/lastUsername';
import { buildUserFromCookieValue, getInitialUserFromCookie } from '../helpers/parseJwt';
import toast from '../helpers/toast';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';
import socket from '../socket/socket';
import { clearPersistedFactiiis } from './useFactiiis';
import useUser from './useUser';

// Base properties shared by both platforms
interface BaseAuthOptions {
  client: WrappedTRPCClient;
}

// Client/Web-specific options
interface ClientAuthOptions extends BaseAuthOptions {
  service: {
    getAcccessToken: () => Promise<string>;
    clearTokens: () => void; // Sync on web
  };
  // Client-specific callbacks
  onLogout?: () => void; // Sync callback for resetConversations
  // Mobile-only property should never exist
  initializePushToken?: never;
}

// Mobile-specific options
interface MobileAuthOptions extends BaseAuthOptions {
  service: {
    clearTokens: () => Promise<void>; // Async on mobile
    getAcccessToken: () => Promise<string>; // Only exists on mobile
    getClientToken: () => Promise<string>; // ft-client cookie value, mirrored from Set-Cookie into AsyncStorage
    getCachedDetails?: () => Promise<string | null>;
    setCachedDetails?: (details: string) => Promise<void>;
    clearCachedDetails?: () => Promise<void>;
  };
  initializePushToken: () => Promise<void>;
  onLogout?: () => Promise<void>; // Async callback for navigation + AsyncStorage
}

// Discriminated union
type UseAuthOptions = ClientAuthOptions | MobileAuthOptions;

// Type guard to check if mobile
function isMobile(options: UseAuthOptions): options is MobileAuthOptions {
  return 'initializePushToken' in options;
}
export default function useAuth(options: UseAuthOptions) {
  const {
    client,
    service,
    onLogout
  } = options;
  const queryClient = useQueryClient();
  const {
    currentUser,
    setIsAuthenticated,
    setUser
  } = useUser();
  const isLoggingOut = useRef(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const isMobilePlatform = isMobile(options);
  const initializeUser = async (): Promise<boolean> => {
    // Mobile: hydrate from AsyncStorage cache for instant UI before network call
    if (isMobilePlatform && options.service.getCachedDetails) {
      try {
        const cached = await options.service.getCachedDetails();
        if (cached) {
          const {
            user
          } = JSON.parse(cached);
          if (user?.id && user?.username) {
            setIsAuthenticated(true);
            setUser({
              ...user,
              isAuthenticated: true
            });
          }
        }
      } catch {
        // Cache read failed — proceed to API call
      }
    }

    // Check if we have a valid token locally before making API calls
    const token = await service.getAcccessToken();
    if (!token) {
      setIsAuthenticated(false);
      setUser({
        ...loggedOutUser,
        id: 0
      });
      return false;
    }
    const [error] = await client.auth.refresh.query();
    if (error) {
      // Client-specific: Handle biometric verification
      if (!isMobilePlatform && error.message.includes('Biometric verification')) {
        setIsAuthenticated(true);
        return true;
      }
      const httpStatus = error.data?.httpStatus;
      const isAuthError = httpStatus === 401 || httpStatus === 403;
      if (isAuthError) {
        setIsAuthenticated(false);
      } else {
        setTimeout(() => {
          if (currentUser.isAuthenticated === null) {
            setIsAuthenticated(false);
          }
        }, 5000);
      }
      return false;
    }

    // Mobile: Initialize push tokens
    if (isMobilePlatform) {
      await options.initializePushToken();
    }
    if (!(await refreshUserFromCookie())) {
      setIsAuthenticated(false);
      return false;
    }
    setIsAuthenticated(true);
    return true;
  };
  const refreshUserFromCookie = async (): Promise<boolean> => {
    let nextUser: ReturnType<typeof getInitialUserFromCookie> = null;
    if (isMobilePlatform) {
      const clientCookie = await options.service.getClientToken();
      nextUser = buildUserFromCookieValue(clientCookie);
    } else {
      nextUser = getInitialUserFromCookie();
    }
    if (!nextUser) return false;
    const merged = {
      ...loggedOutUser,
      ...nextUser
    };
    setUser(merged);

    // The canonical write of the login form's remembered name: this is where the
    // account's real username is known, so it covers the methods that type nothing
    // (passkey autofill, OAuth) and corrects a login done by email address.
    //
    // NOT the only write: initializeUser returns early when the client cookie is
    // not readable yet, so this does not always run on the first sign-in. The login
    // screens also remember what was typed, which is what actually goes back in
    // the field. See shared/utils/helpers/lastUsername.ts.
    void rememberUsername(merged.username);
    if (isMobilePlatform && options.service.setCachedDetails) {
      void options.service.setCachedDetails(JSON.stringify({
        user: merged
      }));
    }
    return true;
  };
  const logout = async (remote = false): Promise<void> => {
    // Prevent concurrent logout calls (handles double triggers from socket + eventEmitter)
    if (isLoggingOut.current) {
      return;
    }
    isLoggingOut.current = true;
    try {
      if ((await service.getAcccessToken()) && !remote) {
        await client.auth.logout.mutate(undefined, {
          context: {
            silenceErrors: true
          }
        });
      }
    } catch (error_0) {
      console.error(error_0);
    }
    await service.clearTokens();

    // Clear cached details on mobile
    if (isMobilePlatform && options.service.clearCachedDetails) {
      await options.service.clearCachedDetails();
    }

    // The persisted factiii list carries this account's pins, so it has to go
    // before the user id is reset — after that there is no key to clear.
    await clearPersistedFactiiis(currentUser.id);

    // Clear state - explicitly set id to 0 to ensure database closes
    // Don't use initialUserState.user — it's computed once at module load from
    // the auth cookie, so it carries the logged-in user's id, avatar, username
    // etc. and spreading it here kept showing the old avatar after logout
    setIsAuthenticated(false);
    setUser({
      ...loggedOutUser,
      id: 0
    });
    //run this first before navigation to prevent refetch loops
    if (onLogout) {
      await onLogout();
    }
    //make sure this happens after you're navigated to Welcome so refetches wont trigger and retun 401s which can put logout in a self loop
    //defer it with timeout so react first updates the context with isauthenticated false and then clear the queries to prevent them triggering again
    setTimeout(() => queryClient.clear(), 0);
    isLoggingOut.current = false;
  };

  // Multi-account bundle helpers. Session ids — bundle lives in the auth JWT cookie.
  const switchTo = async (targetSessionId: number) => {
    setIsSwitching(true);
    try {
      const [error] = await client.auth.switchSession.mutate({
        targetSessionId
      });
      if (error) {
        toast(error.message ?? 'Failed to switch account', '', 'error');
        return false;
      }
      // Server set a fresh AUTH_TOKEN via Set-Cookie — DON'T clearTokens here, that'd wipe it.
      queryClient.clear();
      socket.disconnect();
      await initializeUser();
      socket.connect();
      return true;
    } finally {
      setIsSwitching(false);
    }
  };
  const removeAccount = async (targetSessionId_0: number) => {
    const [error_1] = await client.auth.removeSession.mutate({
      targetSessionId: targetSessionId_0
    });
    if (error_1) {
      toast(error_1.message ?? 'Failed to remove account', '', 'error');
      return false;
    }
    return true;
  };
  return {
    currentUser,
    initializeUser,
    refreshUserFromCookie,
    logout,
    switchTo,
    removeAccount,
    isSwitching
  };
}